import { StringUtil } from "./StringUtil";
import * as Sentry from "@sentry/react";

class LocalStorage {
    private static getStorage(): Storage | null {
        try {
            if (typeof window === "undefined") {
                return null;
            }

            return window.localStorage;
        } catch (error) {
            Sentry.captureException(error, {
                level: "warning",
                tags: {
                    type: "local_storage",
                    operation: "get_storage"
                }
            });
            
            return null;
        }
    }

    static isAvailable(): boolean {
        try {
            const storage = this.getStorage();
            if (!storage) {
                return false;
            }

            const testKey = "__local_storage_test__";
            storage.setItem(testKey, "1");
            const testValue = storage.getItem(testKey);
            storage.removeItem(testKey);
            return testValue === "1";
        } catch (error) {
            Sentry.captureException(error, {
                level: "warning",
                tags: {
                    type: "local_storage",
                    operation: "availability_check"
                }
            });

            return false;
        }
    }

    static set(key: string, value: any, encrypt: boolean = false): void {
        try {
            const storage = this.getStorage();
            if (!storage) {
                return;
            }

            storage.setItem(key, encrypt ? StringUtil.encode(value) : JSON.stringify(value));
        } catch (error) {
            Sentry.captureException(error, {
                level: "warning",
                tags: {
                    type: "local_storage",
                    operation: "set"
                },
                extra: {
                    key
                }
            });
        }
    }

    static get<T = any>(key: string, decrypt: boolean = false): T | null {
        try {
            const storage = this.getStorage();
            if (!storage) {
                return null;
            }

            const item = storage.getItem(key);
            if (!item) {
                return null;
            }

            if (decrypt) {
                return StringUtil.decode(item) as T;
            }

            return JSON.parse(item) as T;
        } catch (error) {
            Sentry.captureException(error, {
                level: "warning",
                tags: {
                    type: "local_storage",
                    operation: "get"
                },
                extra: {
                    key,
                    decrypt
                }
            });

            return null;
        }
    }

    static remove(key: string): boolean {
        try {
            const storage = this.getStorage();
            if (!storage) {
                return false;
            }

            storage.removeItem(key);
            return storage.getItem(key) === null;
        } catch (error) {
            Sentry.captureException(error, {
                level: "warning",
                tags: {
                    type: "local_storage",
                    operation: "remove"
                },
                extra: {
                    key
                }
            });

            return false;
        }
    }

    static removeAll(): void {
        try {
            const storage = this.getStorage();
            if (!storage) {
                return;
            }

            storage.clear();
        } catch (error) {
            Sentry.captureException(error, {
                level: "warning",
                tags: {
                    type: "local_storage",
                    operation: "remove_all"
                }
            });
        }
    }
}

export { LocalStorage }